import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

type CalendarView = "day" | "month" | "year";

const PickerRoot = styled.div`
	display: flex;
	align-items: center;
	width: 100%;
	height: 40px;
	min-width: 0;
	box-sizing: border-box;
	padding: 8px 12px;
	border: 1px solid #cdd4f6;
	border-radius: 8px;
	background: #fff;
	transition:
		border-color 120ms ease,
		box-shadow 120ms ease;
	cursor: pointer;

	&:hover {
		border-color: #8193f1;

		.app-date-picker__suffix {
			opacity: 0;
		}

		.app-date-picker__clear {
			opacity: 1;
			pointer-events: auto;
		}
	}

	&:focus-within,
	&[data-open="true"] {
		border-color: #8193f1;
		box-shadow: 0 4px 12px rgba(21, 89, 165, 0.12);
	}
`;

const PickerInput = styled.input`
	width: 100%;
	min-width: 0;
	padding: 0;
	border: 0;
	outline: 0;
	background: transparent;
	color: #171717;
	font: inherit;
	cursor: pointer;

	&::placeholder {
		color: #a0a0a0;
	}
`;

const PickerAction = styled.span`
	display: grid;
	flex: 0 0 auto;
	place-items: center;
	width: 20px;
	height: 20px;
`;

const Suffix = styled.span`
	display: inline-flex;
	grid-area: 1 / 1;
	align-items: center;
	justify-content: center;
	pointer-events: none;
	transition: opacity 120ms ease;
`;

const ClearButton = styled.button`
	display: inline-grid;
	grid-area: 1 / 1;
	place-items: center;
	width: 16px;
	height: 16px;
	padding: 0;
	border: 0;
	border-radius: 50%;
	background: #a0a0a0;
	color: #fff;
	font: 600 12px/16px var(--font-family-app);
	opacity: 0;
	pointer-events: none;
	cursor: pointer;
	transition:
		background-color 120ms ease,
		opacity 120ms ease;

	&:hover {
		background: #858585;
	}

	&:active {
		background: #666;
	}

	&:focus-visible {
		opacity: 1;
		outline: 2px solid #8193f1;
		outline-offset: 2px;
		pointer-events: auto;
	}
`;

const CalendarPanel = styled.div`
	position: fixed;
	z-index: 2200;
	width: 344px;
	overflow: hidden;
	border: 1px solid #d8ddf8;
	border-radius: 8px;
	background: #fff;
	box-shadow: 0 4px 12px rgba(21, 89, 165, 0.23);
	color: #3b3b3b;
	font: 14px var(--font-family-app);
`;

const Header = styled.div`
	display: grid;
	grid-template-columns: 40px 1fr 1fr 40px;
	align-items: center;
	height: 56px;
	padding: 0 12px;
	border-bottom: 1px solid #ececec;
`;

const HeaderButton = styled.button<{ $active?: boolean }>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 12px;
	height: 40px;
	padding: 0 8px;
	border: 0;
	background: transparent;
	color: ${(props) => (props.$active ? "#f72586" : "#303030")};
	font: 600 14px var(--font-family-app);
	cursor: pointer;
`;

const NavButton = styled(HeaderButton)`
	padding: 0;
`;

const Chevron = styled.span<{ $direction: "up" | "down" | "left" | "right" }>`
	display: inline-block;
	flex: 0 0 auto;
	width: 7px;
	height: 7px;
	border-right: 2px solid currentColor;
	border-bottom: 2px solid currentColor;
	transform: rotate(
		${(props) =>
			({
				up: "225deg",
				down: "45deg",
				left: "135deg",
				right: "-45deg",
			})[props.$direction]}
	);
`;

const TodayButton = styled.button`
	display: block;
	margin: 0 auto;
	padding: 14px 16px 8px;
	border: 0;
	border-radius: 8px;
	background: transparent;
	color: #526dfe;
	font: 600 14px var(--font-family-app);
	cursor: pointer;
	transition:
		background-color 120ms ease,
		color 120ms ease;

	&:hover:not(:disabled) {
		background: #f4f6ff;
		color: #7186ff;
	}

	&:active:not(:disabled) {
		background: #e2e7ff;
		color: #344dcc;
	}

	&:focus-visible {
		outline: 2px solid #8193f1;
		outline-offset: 1px;
	}

	&:disabled {
		color: #b8b8b8;
		cursor: not-allowed;
	}
`;

const Weekdays = styled.div`
	display: grid;
	grid-template-columns: repeat(7, 1fr);
	padding: 22px 22px 4px;
	color: #a0a0a0;
	text-align: center;
	font-size: 12px;
`;

const Days = styled.div`
	display: grid;
	grid-template-columns: repeat(7, 1fr);
	grid-auto-rows: 42px;
	padding: 4px 22px 18px;
`;

const DayButton = styled.button<{ $selected?: boolean; $today?: boolean }>`
	display: grid;
	place-items: center;
	width: 34px;
	height: 34px;
	margin: auto;
	padding: 0;
	border: ${(props) => (props.$selected ? "2px solid #f72586" : "2px solid transparent")};
	border-radius: 50%;
	background: ${(props) => (props.$selected ? "#fff0f7" : "transparent")};
	color: ${(props) => (props.$selected ? "#f72586" : props.$today ? "#526dfe" : "#454545")};
	font: 400 13px var(--font-family-app);
	cursor: pointer;

	&:hover:not(:disabled) {
		background: #fff0f7;
	}

	&:disabled {
		color: #d2d2d2;
		cursor: not-allowed;
	}
`;

const EmptyDay = styled.span``;

const ChoiceGrid = styled.div<{ $columns: number }>`
	display: grid;
	grid-template-columns: repeat(${(props) => props.$columns}, 1fr);
	gap: 8px 4px;
	padding: 16px 22px 24px;
`;

const ChoiceButton = styled.button<{ $selected?: boolean }>`
	min-height: 34px;
	padding: 5px 8px;
	border: ${(props) => (props.$selected ? "2px solid #f72586" : "2px solid transparent")};
	border-radius: 18px;
	background: ${(props) => (props.$selected ? "#fff0f7" : "transparent")};
	color: ${(props) => (props.$selected ? "#f72586" : "#454545")};
	font: ${(props) => (props.$selected ? "600" : "400")} 13px var(--font-family-app);
	cursor: pointer;

	&:hover {
		background: #fff0f7;
	}
`;

export interface AppDatePickerProps {
	value?: Dayjs | null;
	onChange?: (value: Dayjs | null) => void;
	format?: string;
	placeholder?: string;
	disabledDate?: (date: Dayjs) => boolean;
	suffixIcon?: React.ReactNode;
	className?: string;
	"aria-label"?: string;
}

export const AppDatePicker: React.FC<AppDatePickerProps> = ({
	value = null,
	onChange,
	format = "MMM D, YYYY",
	placeholder = "Select date",
	disabledDate,
	suffixIcon,
	className,
	"aria-label": ariaLabel,
}) => {
	const rootRef = useRef<HTMLDivElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const [open, setOpen] = useState(false);
	const [view, setView] = useState<CalendarView>("day");
	const [visibleDate, setVisibleDate] = useState(() => value || dayjs());
	const [position, setPosition] = useState({ top: 0, left: 0 });
	const placementRef = useRef<"top" | "bottom">("bottom");
	const today = dayjs().startOf("day");

	const positionPanel = useCallback(() => {
		const rect = rootRef.current?.getBoundingClientRect();
		if (!rect) return;
		const panelWidth = 344;
		const panelHeight = 420;
		const top = placementRef.current === "bottom" ? rect.bottom + 6 : rect.top - panelHeight - 6;
		setPosition({
			top: Math.max(8, top),
			left: Math.min(Math.max(8, rect.left), window.innerWidth - panelWidth - 8),
		});
	}, []);

	const preparePanelPosition = useCallback(() => {
		const rect = rootRef.current?.getBoundingClientRect();
		if (!rect) return;
		const panelHeight = 420;
		const roomBelow = window.innerHeight - rect.bottom;
		placementRef.current = roomBelow >= panelHeight || rect.top < panelHeight ? "bottom" : "top";
		positionPanel();
	}, [positionPanel]);

	useEffect(() => {
		if (!open) return;
		setVisibleDate(value || dayjs());
		setView("day");
	}, [open, value]);

	useEffect(() => {
		if (!open) return;
		positionPanel();
		const closeOnOutsideClick = (event: MouseEvent) => {
			const target = event.target as Node;
			if (!rootRef.current?.contains(target) && !panelRef.current?.contains(target)) setOpen(false);
		};
		document.addEventListener("mousedown", closeOnOutsideClick);
		window.addEventListener("resize", positionPanel);
		window.addEventListener("scroll", positionPanel, true);
		return () => {
			document.removeEventListener("mousedown", closeOnOutsideClick);
			window.removeEventListener("resize", positionPanel);
			window.removeEventListener("scroll", positionPanel, true);
		};
	}, [open, positionPanel]);

	const days = useMemo(() => {
		const firstDayOffset = visibleDate.startOf("month").day();
		const daysInMonth = visibleDate.daysInMonth();
		return Array.from({ length: Math.ceil((firstDayOffset + daysInMonth) / 7) * 7 }, (_, index) => {
			const number = index - firstDayOffset + 1;
			return number > 0 && number <= daysInMonth ? visibleDate.date(number) : null;
		});
	}, [visibleDate]);

	const yearPageStart = Math.floor(visibleDate.year() / 16) * 16;
	const movePrevious = () => {
		setVisibleDate((current) =>
			view === "day" ? current.subtract(1, "month") : current.subtract(view === "month" ? 1 : 16, "year"),
		);
	};
	const moveNext = () => {
		setVisibleDate((current) =>
			view === "day" ? current.add(1, "month") : current.add(view === "month" ? 1 : 16, "year"),
		);
	};

	const chooseDate = (date: Dayjs) => {
		if (disabledDate?.(date)) return;
		onChange?.(date);
		setOpen(false);
	};

	const panel = open ? (
		<CalendarPanel ref={panelRef} style={position} role="dialog" aria-label="Choose date">
			<Header>
				<NavButton type="button" aria-label="Previous" onClick={movePrevious}>
					<Chevron $direction="left" />
				</NavButton>
				<HeaderButton
					type="button"
					$active={view === "month"}
					onClick={() => setView(view === "month" ? "day" : "month")}
				>
					{visibleDate.format("MMM")} <Chevron $direction={view === "month" ? "up" : "down"} />
				</HeaderButton>
				<HeaderButton
					type="button"
					$active={view === "year"}
					onClick={() => setView(view === "year" ? "day" : "year")}
				>
					{visibleDate.year()} <Chevron $direction={view === "year" ? "up" : "down"} />
				</HeaderButton>
				<NavButton type="button" aria-label="Next" onClick={moveNext}>
					<Chevron $direction="right" />
				</NavButton>
			</Header>

			{view === "day" && (
				<>
					<TodayButton type="button" disabled={disabledDate?.(today)} onClick={() => chooseDate(today)}>
						Today
					</TodayButton>
					<Weekdays>
						{["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
							<span key={`${day}-${index}`}>{day}</span>
						))}
					</Weekdays>
					<Days>
						{days.map((date, index) =>
							date ? (
								<DayButton
									key={date.format("YYYY-MM-DD")}
									type="button"
									$selected={!!value && date.isSame(value, "day")}
									$today={date.isSame(today, "day")}
									disabled={disabledDate?.(date)}
									onClick={() => chooseDate(date)}
								>
									{date.date()}
								</DayButton>
							) : (
								<EmptyDay key={`empty-${index}`} />
							),
						)}
					</Days>
				</>
			)}

			{view === "month" && (
				<ChoiceGrid $columns={3}>
					{Array.from({ length: 12 }, (_, month) => (
						<ChoiceButton
							key={month}
							type="button"
							$selected={month === visibleDate.month()}
							onClick={() => {
								setVisibleDate((current) => current.month(month));
								setView("day");
							}}
						>
							{dayjs().date(1).month(month).format("MMM")}
						</ChoiceButton>
					))}
				</ChoiceGrid>
			)}

			{view === "year" && (
				<ChoiceGrid $columns={4}>
					{Array.from({ length: 16 }, (_, index) => yearPageStart + index).map((year) => (
						<ChoiceButton
							key={year}
							type="button"
							$selected={year === visibleDate.year()}
							onClick={() => {
								setVisibleDate((current) => current.year(year));
								setView("day");
							}}
						>
							{year}
						</ChoiceButton>
					))}
				</ChoiceGrid>
			)}
		</CalendarPanel>
	) : null;

	return (
		<>
			<PickerRoot
				ref={rootRef}
				className={["app-date-picker", className].filter(Boolean).join(" ")}
				data-open={open}
				onClick={() => {
					if (!open) preparePanelPosition();
					setOpen((current) => !current);
				}}
			>
				<PickerInput
					readOnly
					value={value ? value.format(format) : ""}
					placeholder={placeholder}
					aria-label={ariaLabel}
					aria-haspopup="dialog"
					aria-expanded={open}
					onKeyDown={(event) => {
						if (event.key === "Enter" || event.key === " ") {
							event.preventDefault();
							if (!open) preparePanelPosition();
							setOpen((current) => !current);
						}
						if (event.key === "Escape") setOpen(false);
					}}
				/>
				{(suffixIcon || (value && onChange)) && (
					<PickerAction>
						{suffixIcon && <Suffix className="app-date-picker__suffix">{suffixIcon}</Suffix>}
						{value && onChange && (
							<ClearButton
								className="app-date-picker__clear"
								type="button"
								aria-label="Clear date"
								onClick={(event) => {
									event.stopPropagation();
									onChange?.(null);
								}}
							>
								×
							</ClearButton>
						)}
					</PickerAction>
				)}
			</PickerRoot>
			{typeof document !== "undefined" && panel ? createPortal(panel, document.body) : null}
		</>
	);
};
